import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

import TreeView from "./TreeView";
import Legend from "./Legend";

//const baseApiUri = process.env.REACT_APP_API_URL;
const baseDeployApiUri = process.env.REACT_APP_DEPLOY_API_URL;

const PortalAdmin = () => {
  const [treeData, setTreeData] = useState([]);
  const [draggedNode, setDraggedNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [loading, setLoading] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addContext, setAddContext] = useState(null);
  const [elementOptions, setElementOptions] = useState({
    categories: [],
    subcategories: [],
    indicators: [],
  });
  const [submittingAdd, setSubmittingAdd] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editContext, setEditContext] = useState(null);
  // { type, nodeId, ids..., name/title/chart_url/link }
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const { id } = useParams();

  /* -------------------------------------------
     Data Loading (new unified endpoint)
  ------------------------------------------- */
  const loadData = async (id) => {
    try {
      const resp = await axios.get(`${baseDeployApiUri}portal-tree/${id}`, { withCredentials: true });
      console.log("TREE DATA:", resp.data);
      console.log(JSON.stringify(resp.data));
      return resp.data; // { portal, topics: [...] }
    } catch (error) {
      console.error("Error loading portal tree:", error);
      return null;
    }
  };

  const loadElementOptions = async (portalId) => {

    try {
      const resp = await axios.get(`${baseDeployApiUri}portal-elements/${portalId}`, {
        withCredentials: true,
      });

      console.log(`Element Options for portal ${portalId}`, JSON.stringify(resp.data));
      
      setElementOptions({
        categories: resp.data?.categories || [],
        subcategories: resp.data?.subcategories || [],
        indicators: resp.data?.indicators || [],
      });
    } catch (e) {
      console.error("Failed to load element options", e);
    }
  };

  useEffect(() => {
    async function init() {
      setLoading(true);
      const data = await loadData(id);
      if (data) {
        const tree = buildTreeStructure(data);
        setTreeData(tree);
      }
      await loadElementOptions(id);
      setLoading(false);
    }
    init();
  }, [id]);

  /* -------------------------------------------
     Drag + Drop
  ------------------------------------------- */
  const handleDragStart = (node) => setDraggedNode(node);

  const handleDrop = async (targetNode) => {
    if (!draggedNode || draggedNode.id === targetNode.id) return;
    const draggedType = getNodeType(draggedNode.id);
    const targetType = getNodeType(targetNode.id);

    if (!isValidDrop(draggedType, targetType)) {
      console.warn("Invalid drop between", draggedType, "→", targetType);
      return;
    }

    try {
      const payload = {
        draggedId: draggedNode.id,
        targetId: targetNode.id,
        portal: id,
      };
      const resp = await axios.post(`${baseDeployApiUri}portal-element-edit`, payload, {
        withCredentials: true,
      });
      console.log("MOVE", resp.data);

      const updatedTree = moveNode(treeData, draggedNode.id, targetNode.id);
      setTreeData(updatedTree);
      setDraggedNode(null);
    } catch (err) {
      console.error("Error during reorder:", err);
    }
  };

  const handleToggle = (nodeId) =>
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));

  /* -------------------------------------------
     Topics
  ------------------------------------------- */
  const handleAddTopic = async () => {
    try {
      const newName = prompt("Enter a name for the new topic:", "New Topic");
      if (!newName) return;
      const payload = { portalId: Number(id), name: newName };

      const resp = await axios.post(`${baseDeployApiUri}topics`, payload, {
        withCredentials: true,
      });

      if (resp.data?.success && resp.data.node) {
        setTreeData((prev) => {
          const copy = JSON.parse(JSON.stringify(prev));
          const portalNode = copy[0];
          portalNode.children = portalNode.children || [];
          portalNode.children.push(resp.data.node);
          return copy;
        });
      }
    } catch (err) {
      console.error("Error adding topic:", err);
      alert("Failed to create topic");
    }
  };

  const handleDeleteTopic = async (topicNode) => {
    console.log('Deleting Topic'); 
    //return;
    const match = topicNode.id.match(/_t(\d+)/);
    const topicId = match ? Number(match[1]) : null;
    if (!topicId) return alert("Could not determine topic id.");

    if (
      !window.confirm(
        "Delete this topic and all related categories, subcategories, and indicators?"
      )
    )
      return;

    try {
      const resp = await axios.delete(`${baseDeployApiUri}portal-element-delete`, {
        data: {
          portalId: id,
          elementType: "topic",
          elementId: topicId,
        },
        withCredentials: true,
      });

      if (resp.data?.success) {
        setTreeData((prev) => removeNodeById(prev, topicNode.id));
      } else {
        alert("Failed to delete topic");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Error deleting topic");
    }
  };

  const handleDeleteCategory = async (categoryNode) => {
    console.log('Deleting Category'); 
    //return;
  const match = categoryNode.id.match(/_c(\d+)/);
  const categoryId = match ? Number(match[1]) : null;
  if (!categoryId) return alert("Could not determine category id.");

  if (!window.confirm("Unlink this category from the portal?")) return;

  try {
    const resp = await axios.delete(`${baseDeployApiUri}portal-element-delete`, {
      data: {
        portalId: id,
        elementType: "category",
        elementId: categoryId,
      },
      withCredentials: true,
    });

    if (resp.data?.success) {
      setTreeData((prev) => removeNodeById(prev, categoryNode.id));
    } else {
      alert("Failed to delete category");
    }
  } catch (err) {
    console.error("Delete failed:", err);
    alert("Error deleting category");
  }
};

const handleDeleteSubcategory = async (subNode) => {
  console.log('Delete subcategory'); 
  //return;
  const match = subNode.id.match(/_s(\d+)/);
  const subId = match ? Number(match[1]) : null;
  if (!subId) return alert("Could not determine subcategory id.");

  if (!window.confirm("Unlink this subcategory from the portal?")) return;

  try {
    const resp = await axios.delete(`${baseDeployApiUri}portal-element-delete`, {
      data: {
        portalId: id,
        elementType: "subcategory",
        elementId: subId,
      },
      withCredentials: true,
    });

    if (resp.data?.success) {
      setTreeData((prev) => removeNodeById(prev, subNode.id));
    } else {
      alert("Failed to delete subcategory");
    }
  } catch (err) {
    console.error("Delete failed:", err);
    alert("Error deleting subcategory");
  }
};


  const handleDeleteIndicator = async (indicatorNode) => {
    console.log('Delete indicator'); 
    //return;
  const match = indicatorNode.id.match(/_i(\d+)/);
  const indicatorId = match ? Number(match[1]) : null;
  if (!indicatorId) return alert("Could not determine indicator id.");

  const confirmed = window.confirm("Are you sure you want to delete this indicator?");
  if (!confirmed) return;

  const { topicId, categoryId, subcategoryId } = parseIds(indicatorNode.id);
  console.log("Deleting indicator with context:", {
  indicatorId,
  topicId,
  categoryId,
  subcategoryId,
});



  try {
    const resp = await axios.delete(`${baseDeployApiUri}portal-element-delete`, {
      data: {
    portalId: id,
    elementType: "indicator",
    elementId: indicatorId,
    topicId,
    categoryId,
    subcategoryId,
  },
      withCredentials: true,
    });

    console.log("DELETE INDICATOR:", resp.data);

    if (resp.data?.success) {
      alert("Indicator deleted successfully");
      setTreeData((prev) => removeNodeById(prev, indicatorNode.id));
    } else {
      alert("Failed to delete indicator");
    }
  } catch (err) {
    console.error("Delete indicator failed:", err);
    alert("Error deleting indicator");
  }
};


  /* -------------------------------------------
     Add UI Handlers
  ------------------------------------------- */
  const parseIds = (identifier) => {
    const t = identifier.match(/_t(\d+)/);
    const c = identifier.match(/_c(\d+)/);
    const s = identifier.match(/_s(\d+)/);
    return {
      topicId: t ? Number(t[1]) : null,
      categoryId: c ? Number(c[1]) : null,
      subcategoryId: s ? Number(s[1]) : null,
    };
  };


const parseIndicatorId = (nodeId) => {
  const m = nodeId.match(/_i(\d+)/);
  return m ? Number(m[1]) : null;
};

const handleEditNodeUI = (node) => {
  const type = getNodeType(node.id);

  if (type === "portal") return;

  if (type === "indicator") {
    const indicatorId = node.indicatorId ?? parseIndicatorId(node.id);

    setEditContext({
      type,
      nodeId: node.id,
      indicatorId,
      title: node.title ?? node.label ?? "",
      chart_url: node.chart_url ?? "",
      link: node.link ?? "",
    });
  } else {
    // topic/category/subcategory
    const ids = parseIds(node.id);
    setEditContext({
      type,
      nodeId: node.id,
      ...ids,
      name: node.label ?? "",
    });
  }

  setEditModalOpen(true);
};

  const handleAddCategoryUI = (topicNode) => {
    const { topicId } = parseIds(topicNode.id);
    setAddContext({
      type: "category",
      parent: { id: topicNode.id, topicId },
      mode: "new",
      name: "",
      existingId: null,
    });
    setAddModalOpen(true);
  };

  const handleAddSubcategoryUI = (categoryNode) => {
    const { topicId, categoryId } = parseIds(categoryNode.id);
    setAddContext({
      type: "subcategory",
      parent: { id: categoryNode.id, topicId, categoryId },
      mode: "new",
      name: "",
      existingId: null,
    });
    setAddModalOpen(true);
  };

  const handleAddIndicatorUI = (parentNode) => {
    const ids = parseIds(parentNode.id);
    setAddContext({
      type: "indicator",
      parent: {
        id: parentNode.id,
        topicId: ids.topicId,
        categoryId: ids.categoryId,
        subcategoryId: ids.subcategoryId,
      },
      mode: "new",
      name: "",
      url: "",
      link: "",
      existingId: null,
    });
    setAddModalOpen(true);
  };

  const submitEdit = async () => {
  if (!editContext) return;

  setSubmittingEdit(true);

  try {
if (editContext.type === "indicator") {
  const payload = {
    indicatorId: editContext.indicatorId,
    title: (editContext.title || "").trim(),
    chart_url: (editContext.chart_url || "").trim(),
    link: (editContext.link || "").trim(),
  };

  if (!payload.title) return alert("Title is required.");
  if (!payload.chart_url) return alert("Chart URL is required.");

  const resp = await axios.post(`${baseDeployApiUri}portal-indicator-update`, payload, {
    withCredentials: true,
  });

  if (!resp.data?.success) {
    alert("Failed to update indicator.");
    return;
  }

  // Update tree label + cached fields
  setTreeData((prev) => {
    const copy = JSON.parse(JSON.stringify(prev));
    const update = (nodes) => {
      for (const n of nodes) {
        if (n.id === editContext.nodeId) {
          n.label = payload.title;
          n.title = payload.title;
          n.chart_url = payload.chart_url;
          n.link = payload.link;
          return true;
        }
        if (n.children && update(n.children)) return true;
      }
      return false;
    };
    update(copy);
    return copy;
  });
}
 else {
const name = (editContext.name || "").trim();
  if (!name) return alert("Name is required.");

  // Only wire TOPIC updates for now
  if (editContext.type === "topic") {
    const payload = {
      portalId: Number(id),
      topicId: editContext.topicId,
      name,
    };

    const resp = await axios.post(`${baseDeployApiUri}portal-topic-update`, payload, {
      withCredentials: true,
    });

    if (!resp.data?.success) {
      alert("Failed to update topic.");
      return;
    }
  } else if (editContext.type === "subcategory") {
  const payload = {
    portalId: Number(id),
    subcategoryId: editContext.subcategoryId,
    name,
  };

  const resp = await axios.post(`${baseDeployApiUri}portal-subcategory-update`, payload, {
    withCredentials: true,
  });

  if (!resp.data?.success) {
    alert("Failed to update subcategory.");
    return;
  }
} else if (editContext.type === "category") {
  const payload = {
    portalId: Number(id),
    categoryId: editContext.categoryId,
    name,
  };

  const resp = await axios.post(`${baseDeployApiUri}portal-category-update`, payload, {
    withCredentials: true,
  });

  if (!resp.data?.success) {
    alert("Failed to update category.");
    return;
  }
}

else {
    // category/subcategory will be wired later
    console.log("Would update node with:", {
      portalId: Number(id),
      type: editContext.type,
      topicId: editContext.topicId,
      categoryId: editContext.categoryId,
      subcategoryId: editContext.subcategoryId,
      name,
    });
  }

  // Update tree label locally (works for topic/category/subcategory)
  setTreeData((prev) => {
    const copy = JSON.parse(JSON.stringify(prev));
    const update = (nodes) => {
      for (const n of nodes) {
        if (n.id === editContext.nodeId) {
          n.label = name;
          return true;
        }
        if (n.children && update(n.children)) return true;
      }
      return false;
    };
    update(copy);
    return copy;
  });
    }

    setEditModalOpen(false);
    setEditContext(null);
  } finally {
    setSubmittingEdit(false);
  }
};


  /* -------------------------------------------
     Submit Add
  ------------------------------------------- */



/* -------------------------------------------
   Add Category/Subcategory — submit
------------------------------------------- */
const submitAdd = async () => {
  if (!addContext) return;

  // === Basic validations
  if (addContext.mode === "new" && !addContext.name.trim()) {
    alert("Please enter a name.");
    return;
  }
  if (addContext.mode === "reuse" && !addContext.existingId) {
    alert("Please select an existing item to reuse.");
    return;
  }

  setSubmittingAdd(true);

  try {
    let endpoint = "";
    let payload = {};

    // 🟢 CATEGORY
    if (addContext.type === "category") {
      endpoint = `${baseDeployApiUri}portal-element-add`;
      payload =
        addContext.mode === "new"
          ? {
              portalId: Number(id),
              elementType: "category",
              mode: "new",
              name: addContext.name.trim(),
              parentTopicId: addContext.parent.topicId,
            }
          : {
              portalId: Number(id),
              elementType: "category",
              mode: "reuse",
              existingId: addContext.existingId,
              parentTopicId: addContext.parent.topicId,
            };
    }

    // 🟢 SUBCATEGORY
    else if (addContext.type === "subcategory") {
      endpoint = `${baseDeployApiUri}portal-element-add`;
      payload =
        addContext.mode === "new"
          ? {
              portalId: Number(id),
              elementType: "subcategory",
              mode: "new",
              name: addContext.name.trim(),
              parentTopicId: addContext.parent.topicId,
              parentCategoryId: addContext.parent.categoryId,
            }
          : {
              portalId: Number(id),
              elementType: "subcategory",
              mode: "reuse",
              existingId: addContext.existingId,
              parentTopicId: addContext.parent.topicId,
              parentCategoryId: addContext.parent.categoryId,
            };
    }

    // 🟣 INDICATOR (new or reuse)
    else if (addContext.type === "indicator") {
      endpoint = `${baseDeployApiUri}portal-indicator-add`;
      payload =
        addContext.mode === "new"
          ? {
              portalId: Number(id),
              parentTopicId: addContext.parent.topicId,
              parentCategoryId: addContext.parent.categoryId,
              parentSubcategoryId: addContext.parent.subcategoryId,
              mode: "new",
              title: addContext.name.trim(),
              chart_url: addContext.url || "",
              link: addContext.link || "",
            }
          : {
              portalId: Number(id),
              parentTopicId: addContext.parent.topicId,
              parentCategoryId: addContext.parent.categoryId,
              parentSubcategoryId: addContext.parent.subcategoryId,
              mode: "reuse",
              existingId: addContext.existingId,
            };
    }

    // 🔵 Make API request
    const resp = await axios.post(endpoint, payload, { withCredentials: true });
    console.log("Add response:", resp.data);

    const node = resp.data?.node || resp.data?.data?.node;

    // 🔵 Update tree in-place
    if (resp.data?.success && node) {
      setTreeData((prev) => {
        const copy = JSON.parse(JSON.stringify(prev));

        const targetId =
          addContext.type === "category"
            ? `p${id}_t${addContext.parent.topicId}`
            : addContext.type === "subcategory"
            ? `p${id}_t${addContext.parent.topicId}_c${addContext.parent.categoryId}`
            : // for indicators
              addContext.parent.id;

        const insertNode = (nodes) => {
          for (const n of nodes) {
            if (n.id === targetId) {
              n.children = n.children || [];
              n.children.push(node);
              return true;
            }
            if (n.children && insertNode(n.children)) return true;
          }
          return false;
        };

        insertNode(copy);
        return copy;
      });
    } else {
      console.warn("Unexpected response:", resp.data);
      alert("Failed to add element. Check console for details.");
    }
  } catch (e) {
    console.error("Add element failed", e);
    alert("Failed to add element");
  } finally {
    setSubmittingAdd(false);
    setAddModalOpen(false);
    setAddContext(null);
  }
};



  /* -------------------------------------------
     Helpers
  ------------------------------------------- */
  const getNodeType = (id) => {
    if (id.includes("_i")) return "indicator";
    if (id.includes("_s")) return "subcategory";
    if (id.includes("_c")) return "category";
    if (id.includes("_t")) return "topic";
    return "portal";
  };

  const isValidDrop = (dragged, target) => {
    if (dragged === "indicator") return true;
    if (dragged === "subcategory" && target === "category") return true;
    if (dragged === "category" && target === "topic") return true;
    if (dragged === "topic" && target === "portal") return true;
    return false;
  };

  const moveNode = (tree, draggedId, targetId) => {
    const copy = JSON.parse(JSON.stringify(tree));
    let draggedNode = null;
    const remove = (nodes) => {
      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].id === draggedId) {
          draggedNode = nodes.splice(i, 1)[0];
          return true;
        }
        if (nodes[i].children && remove(nodes[i].children)) return true;
      }
      return false;
    };
    const insert = (nodes) => {
      for (const n of nodes) {
        if (n.id === targetId) {
          n.children = n.children || [];
          n.children.push(draggedNode);
          return true;
        }
        if (n.children && insert(n.children)) return true;
      }
      return false;
    };
    remove(copy);
    insert(copy);
    return copy;
  };

  const removeNodeById = (tree, id) =>
    tree
      .map((n) => {
        if (n.id === id) return null;
        if (n.children) n.children = removeNodeById(n.children, id);
        return n;
      })
      .filter(Boolean);

  /* -------------------------------------------
     Render
  ------------------------------------------- */
  return (
    <div style={{ padding: 24, background: "white", borderRadius: 8 }}>
      <h2 style={{ fontSize: 24, fontWeight: "bold", marginBottom: 8 }}>
        Portal Tree Management
      </h2>

      <button
        onClick={handleAddTopic}
        style={{
          background: "#3b82f6",
          color: "white",
          border: "none",
          padding: "8px 12px",
          borderRadius: 4,
          cursor: "pointer",
          marginBottom: 16,
        }}
      >
        + Add New Topic
      </button>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <TreeView
            treeData={treeData}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
            onToggle={handleToggle}
            expandedNodes={expandedNodes}
            onDeleteTopic={handleDeleteTopic}
            onDeleteIndicator={handleDeleteIndicator} 
            onDeleteCategory={handleDeleteCategory}
            onDeleteSubcategory={handleDeleteSubcategory}
            onAddCategory={handleAddCategoryUI}
            onAddSubcategory={handleAddSubcategoryUI}
            onAddIndicator={handleAddIndicatorUI}
            onEditNode={handleEditNodeUI}
          />
          <Legend />
        </>
      )}

      {editModalOpen && editContext && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.25)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    }}
  >
    <div
      style={{
        background: "white",
        padding: 16,
        borderRadius: 8,
        width: 440,
        boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: 10 }}>
        Edit {editContext.type.charAt(0).toUpperCase() + editContext.type.slice(1)}
      </h3>

      {editContext.type === "indicator" ? (
        <>
          <input
            placeholder="Indicator title"
            value={editContext.title || ""}
            onChange={(e) => setEditContext((c) => ({ ...c, title: e.target.value }))}
            style={{ width: "100%", marginBottom: 8, padding: "8px 10px" }}
          />
          <input
            placeholder="Chart URL"
            value={editContext.chart_url || ""}
            onChange={(e) => setEditContext((c) => ({ ...c, chart_url: e.target.value }))}
            style={{ width: "100%", marginBottom: 8, padding: "8px 10px" }}
          />
          <input
            placeholder="Link (optional)"
            value={editContext.link || ""}
            onChange={(e) => setEditContext((c) => ({ ...c, link: e.target.value }))}
            style={{ width: "100%", marginBottom: 12, padding: "8px 10px" }}
          />
        </>
      ) : (
        <input
          placeholder="Name"
          value={editContext.name || ""}
          onChange={(e) => setEditContext((c) => ({ ...c, name: e.target.value }))}
          style={{ width: "100%", marginBottom: 12, padding: "8px 10px" }}
        />
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          onClick={() => {
            setEditModalOpen(false);
            setEditContext(null);
          }}
          style={{ padding: "6px 10px" }}
          disabled={submittingEdit}
        >
          Cancel
        </button>
        <button
          onClick={submitEdit}
          style={{
            padding: "6px 10px",
            background: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
            opacity: submittingEdit ? 0.7 : 1,
          }}
          disabled={submittingEdit}
        >
          {submittingEdit ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  </div>
)}


      {/* Add Modal */}
      {addModalOpen && addContext && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              padding: 16,
              borderRadius: 8,
              width: 440,
              boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 10 }}>
              Add {addContext.type.charAt(0).toUpperCase() + addContext.type.slice(1)}
            </h3>

            <div style={{ marginBottom: 10, display: "flex", gap: 16 }}>
              <label>
                <input
                  type="radio"
                  checked={addContext.mode === "new"}
                  onChange={() =>
                    setAddContext((c) => ({ ...c, mode: "new", existingId: null }))
                  }
                />{" "}
                Create New
              </label>
              <label>
                <input
                  type="radio"
                  checked={addContext.mode === "reuse"}
                  onChange={() =>
                    setAddContext((c) => ({ ...c, mode: "reuse", name: "" }))
                  }
                />{" "}
                Reuse Existing
              </label>
            </div>

            {/* Input Fields */}
            {addContext.type === "indicator" ? (
              <>
                {addContext.mode === "new" ? (
                  <>
                    <input
                      placeholder="Indicator title"
                      value={addContext.name || ""}
                      onChange={(e) =>
                        setAddContext((c) => ({ ...c, name: e.target.value }))
                      }
                      style={{ width: "100%", marginBottom: 8, padding: "8px 10px" }}
                    />
                    <input
                      placeholder="Indicator URL"
                      value={addContext.url || ""}
                      onChange={(e) =>
                        setAddContext((c) => ({ ...c, url: e.target.value }))
                      }
                      style={{ width: "100%", marginBottom: 8, padding: "8px 10px" }}
                    />
                    <input
                      placeholder="Indicator link (optional)"
                      value={addContext.link || ""}
                      onChange={(e) =>
                        setAddContext((c) => ({ ...c, link: e.target.value }))
                      }
                      style={{ width: "100%", marginBottom: 12, padding: "8px 10px" }}
                    />
                  </>
                ) : (
                  <select
                    value={addContext.existingId || ""}
                    onChange={(e) =>
                      setAddContext((c) => ({ ...c, existingId: Number(e.target.value) }))
                    }
                    style={{ width: "100%", marginBottom: 12, padding: "8px 10px" }}
                  >
                    <option value="" disabled>
                      Select existing indicator
                    </option>
                    {elementOptions.indicators.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.title}
                      </option>
                    ))}
                  </select>
                )}
              </>
            ) : addContext.mode === "new" ? (
              <input
                placeholder={`New ${addContext.type} name`}
                value={addContext.name || ""}
                onChange={(e) =>
                  setAddContext((c) => ({ ...c, name: e.target.value }))
                }
                style={{ width: "100%", marginBottom: 12, padding: "8px 10px" }}
              />
            ) : (
              <select
                value={addContext.existingId || ""}
                onChange={(e) =>
                  setAddContext((c) => ({ ...c, existingId: Number(e.target.value) }))
                }
                style={{ width: "100%", marginBottom: 12, padding: "8px 10px" }}
              >
                <option value="" disabled>
                  Select existing {addContext.type}
                </option>
                {(addContext.type === "category"
                  ? elementOptions.categories
                  : elementOptions.subcategories
                ).map((opt) => {
                  const key =
                    addContext.type === "category"
                      ? opt.id_category
                      : opt.id_subcategory;
                  return (
                    <option key={key} value={key}>
                      {opt.name}
                    </option>
                  );
                })}
              </select>
            )}

            {/* Buttons */}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setAddModalOpen(false);
                  setAddContext(null);
                }}
                style={{ padding: "6px 10px" }}
                disabled={submittingAdd}
              >
                Cancel
              </button>
              <button
                onClick={submitAdd}
                style={{
                  padding: "6px 10px",
                  background: "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  opacity: submittingAdd ? 0.7 : 1,
                }}
                disabled={submittingAdd}
              >
                {submittingAdd ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------
   Helper: Build Tree
------------------------------------------- */
function buildTreeStructure(data) {
  const safeLabel = (v) => (v && v.trim() !== "" ? v : "[NO LABEL]");
  const portalId = data.portal.id;
  const portalNode = {
    id: `p${portalId}`,
    label: safeLabel(data.portal.name),
    children: [],
  };

  (data.topics || []).forEach((topic) => {
    const topicNode = {
      id: `p${portalId}_t${topic.id_topic}`,
      label: safeLabel(topic.name),
      children: [],
    };

    // add topic-level indicators
    (topic.indicators || []).forEach((ind) => {
      topicNode.children.push({
        id: `p${portalId}_t${topic.id_topic}_i${ind.id}`,
        label: safeLabel(ind.title),
        indicatorId: ind.id,
        title: ind.title || "",
        chart_url: ind.chart_url || "",
        link: ind.link || "",
      });
    });

    // add categories
    (topic.categories || []).forEach((cat) => {
      const catNode = {
        id: `p${portalId}_t${topic.id_topic}_c${cat.id_category}`,
        label: safeLabel(cat.name),
        children: [],
      };

      // add subcategories
      (cat.subcategories || []).forEach((sub) => {
        const subNode = {
          id: `p${portalId}_t${topic.id_topic}_c${cat.id_category}_s${sub.id_subcategory}`,
          label: safeLabel(sub.name),
          children: [],
        };

        // add indicators under subcategory
        (sub.indicators || []).forEach((ind) => {
          subNode.children.push({
            id: `p${portalId}_t${topic.id_topic}_c${cat.id_category}_s${sub.id_subcategory}_i${ind.id}`,
            label: safeLabel(ind.title),
            indicatorId: ind.id,
            title: ind.title || "",
            chart_url: ind.chart_url || "",
            link: ind.link || "",
          });
        });

        catNode.children.push(subNode);
      });

      // add indicators directly under category
      (cat.indicators || []).forEach((ind) => {
        catNode.children.push({
          id: `p${portalId}_t${topic.id_topic}_c${cat.id_category}_i${ind.id}`,
          label: safeLabel(ind.title),
          indicatorId: ind.id,
          title: ind.title || "",
          chart_url: ind.chart_url || "",
          link: ind.link || "",
        });
      });

      topicNode.children.push(catNode);
    });

    portalNode.children.push(topicNode);
  });

  return [portalNode];
}



export default PortalAdmin;
